import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Wip = () => {
  // State variables for WIP calculation
  const [beginningWIP, setBeginningWIP] = useState(0); // Beginning WIP
  const [manufacturingCosts, setManufacturingCosts] = useState(0); // Manufacturing Costs
  const [completedGoods, setCompletedGoods] = useState(0); // Completed Goods
  const [wipResult, setWipResult] = useState(0); // Calculated WIP

  // Event handlers to update state values
  const handleBeginningWIPChange = (e) =>
    setBeginningWIP(parseFloat(e.target.value) || 0);
  const handleManufacturingCostsChange = (e) =>
    setManufacturingCosts(parseFloat(e.target.value) || 0);
  const handleCompletedGoodsChange = (e) =>
    setCompletedGoods(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate WIP when any input changes
  useEffect(() => {
    const calculatedWIP = beginningWIP + manufacturingCosts - completedGoods;
    setWipResult(calculatedWIP);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [beginningWIP, manufacturingCosts, completedGoods]);

  return (
    <Layout title="Work-in-Progress (WIP) Calculator" path="wip-calculator">
      <section className="section-padding" id="wip-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* WIP Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Work-in-Progress (WIP) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Beginning WIP Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="beginningWIP" className="form-label">
                        Beginning WIP
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Beginning WIP:</strong> Enter the value of work that was in progress at the start of the period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="beginningWIP"
                        placeholder="Enter beginning WIP value"
                        value={beginningWIP || ""}
                        onChange={handleBeginningWIPChange}
                      />
                    </div>

                    {/* Manufacturing Costs Input */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="manufacturingCosts"
                        className="form-label"
                      >
                        Manufacturing Costs
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Manufacturing Costs:</strong> Enter the total costs incurred for manufacturing during the period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="manufacturingCosts"
                        placeholder="Enter manufacturing costs"
                        value={manufacturingCosts || ""}
                        onChange={handleManufacturingCostsChange}
                      />
                    </div>

                    {/* Completed Goods Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="completedGoods" className="form-label">
                        Completed Goods
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Completed Goods:</strong> Enter the value of goods completed during the period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="completedGoods"
                        placeholder="Enter completed goods value"
                        value={completedGoods || ""}
                        onChange={handleCompletedGoodsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        WIP:{" "}
                        <span className="fw-bold ms-3">
                          {wipResult === 0
                            ? "0.00"
                            : wipResult.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Work-in-Progress (WIP) Calculator
                </h3>

                {/* What is a WIP Calculator? */}
                <h4 className="mt-3 mb-3">What is a WIP Calculator?</h4>
                <p>
                  The **Work-in-Progress (WIP) Calculator** helps businesses
                  calculate the value of products that are still in the
                  production process and not yet completed. By using the WIP
                  formula, it provides a snapshot of the ongoing manufacturing
                  process.
                </p>

                {/* Why Use a WIP Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a WIP Calculator?</h4>
                <p>
                  The WIP calculator is essential for manufacturing businesses,
                  as it helps in assessing the amount of work completed and
                  unfinished. This information is crucial for inventory
                  management, cost control, and making accurate financial
                  reports.
                </p>

                {/* Benefits of Using a WIP Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a WIP Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Provides insight into production efficiency:
                    </strong>{" "}
                    By calculating WIP, businesses can assess the effectiveness
                    of their production process.
                  </li>
                  <li>
                    <strong>
                      Helps in inventory management and cost analysis:
                    </strong>{" "}
                    Accurate WIP values allow businesses to optimize inventory
                    levels and track production costs.
                  </li>
                  <li>
                    <strong>Useful for accurate financial reporting:</strong>{" "}
                    WIP values are often included in financial statements and
                    are crucial for providing an accurate view of a business’s
                    financial health.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a WIP Calculator?</h4>
                <p>The WIP Calculator is ideal for:</p>
                <ul>
                  <li>
                    <strong>Manufacturing businesses:</strong> To calculate the
                    value of products in progress and optimize production
                    workflows.
                  </li>
                  <li>
                    <strong>Supply chain managers:</strong> To track inventory
                    and ensure efficient production schedules.
                  </li>
                  <li>
                    <strong>Accountants:</strong> For financial reporting and
                    cost analysis.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How Does WIP Affect Financial Reporting?
                </h4>
                <p>
                  WIP is an important component of a company’s balance sheet. It
                  affects the calculation of cost of goods sold (COGS) and helps
                  ensure that financial statements reflect the true value of
                  production at any given point in time.
                </p>

                <h4 className="mt-4 mb-3">
                  What Should I Do If WIP is Too High?
                </h4>
                <p>
                  High WIP levels can indicate inefficiencies in production.
                  Businesses should analyze their production process to identify
                  bottlenecks and reduce excess inventory that could tie up
                  resources and increase costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Wip;
